import type { InputPayload } from "../types";

export interface TriggerRunFormData {
  directoryPath: string;
  scale: "standard" | "enterprise";
  shardSize: string;
  maxParallelShards: string;
  timeoutMinutes: string;
  circuitBreakerThreshold: string;
  circuitBreakerSampleSize: string;
  aiAnalysisEnabled: boolean;
  customAnalysisPrompt: string;
}

export const INITIAL_FORM_DATA: TriggerRunFormData = {
  directoryPath: "",
  scale: "standard",
  shardSize: "",
  maxParallelShards: "",
  timeoutMinutes: "",
  circuitBreakerThreshold: "",
  circuitBreakerSampleSize: "",
  aiAnalysisEnabled: false,
  customAnalysisPrompt: "",
};

function parseOptionalInt(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : undefined;
}

function parseOptionalFloat(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Remove keys with undefined values so they are omitted from JSON. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as T;
}

export function buildInputPayload(
  formData: TriggerRunFormData,
  orgCategory: string,
  orgId: string,
): InputPayload {
  const common = {
    org_category: orgCategory,
    org_id: orgId,
    directory_path: formData.directoryPath,
    persist_batch_summary: true as const,
    timeout_minutes: parseOptionalInt(formData.timeoutMinutes),
    circuit_breaker_threshold: parseOptionalFloat(
      formData.circuitBreakerThreshold,
    ),
    circuit_breaker_sample_size: parseOptionalInt(
      formData.circuitBreakerSampleSize,
    ),
    trigger_analysis: formData.aiAnalysisEnabled || undefined,
    analysis_system_prompt: formData.aiAnalysisEnabled
      ? formData.customAnalysisPrompt || undefined
      : undefined,
  };

  if (formData.scale === "enterprise") {
    return stripUndefined({
      workflow: "shadow_run_orchestrator" as const,
      ...common,
      shard_size: parseOptionalInt(formData.shardSize),
      max_parallel_shards: parseOptionalInt(formData.maxParallelShards),
    });
  }

  return stripUndefined({
    workflow: "shadow_run" as const,
    ...common,
  });
}
